import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
    Modal,
    Spin,
    Typography,
    Steps,
    Form,
    Input,
    Select,
    Button,
    Upload,
    Radio,
    message,
    AutoComplete,
    Row,
    Col,
    Divider,
    Card,
    Progress,
    Space,
    Alert,
    Badge,
    Result,
    App,
    Drawer,
    ConfigProvider,
    Grid,
    Segmented
} from 'antd';
import {
    CheckCircleOutlined,
    CloudUploadOutlined,
    LoadingOutlined,
    UserOutlined,
    CarOutlined,
    FileTextOutlined,
    BankOutlined,
    HomeOutlined,
    IdcardOutlined,
    ClockCircleOutlined,
    CameraOutlined,
    FileImageOutlined,
    SafetyOutlined,
    MedicineBoxOutlined,
    SolutionOutlined,
    RightOutlined,
    CheckOutlined,
    FileOutlined
} from '@ant-design/icons';
import { useAuth } from './AuthContext';
import { doc, getDoc, setDoc, collection, getDocs, onSnapshot } from 'firebase/firestore';
import { db, storage } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import Lottie from 'lottie-react';
import successAnimation from '../components/lottie/success.json';
import awaitingApprovalAnimation from '../components/lottie/isProcessing.json';
import DocumentSigning from './DocumentSigning.js';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { generateDocumentsForUser } from '../components/utils/documentGenerator';

const { Step } = Steps;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const UserStatusContext = createContext();

export const useUserStatus = () => {
    return useContext(UserStatusContext);
};

// Mobile-optimized styles
const mobileStyles = {
    container: {
        padding: '16px',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
    },
    card: {
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        marginBottom: '16px',
        border: 'none'
    },
    button: {
        height: '48px',
        fontSize: '16px',
        borderRadius: '24px',
        fontWeight: '600'
    },
    uploadButton: {
        minHeight: '120px',
        width: '100%',
        minWidth: '90vw',
        maxWidth: '90vw',
        borderRadius: '12px',
        border: '2px dashed #d9d9d9',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        backgroundColor: '#fafafa',
        padding: '16px'
    },
    input: {
        height: '48px',
        fontSize: '16px',
        borderRadius: '8px'
    },
    radioCard: {
        padding: '20px',
        borderRadius: '12px',
        border: '2px solid #f0f0f0',
        marginBottom: '12px',
        cursor: 'pointer',
        transition: 'all 0.3s'
    },
    stepIndicator: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: '#fff',
        zIndex: 10,
        padding: '12px 16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        minHeight: '86px',
        display: 'flex',
        alignItems: 'center'
    },
    radioGroup: {
        width: '100%',
        display: 'block'
    }
};

export const UserStatusProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const screens = useBreakpoint();
    const [userStatus, setUserStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [isSdkInitialized, setIsSdkInitialized] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [carOwnership, setCarOwnership] = useState(null);
    const [urzedyOptions, setUrzedyOptions] = useState([]);
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSumsubModalVisible, setIsSumsubModalVisible] = useState(false);
    const [isLoadingSumsub, setIsLoadingSumsub] = useState(false);
    const [countdown, setCountdown] = useState(60);
    const [isSavingData, setIsSavingData] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [messageApi, contextHolder] = message.useMessage();
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [selectedFiles, setSelectedFiles] = useState({});
    const [isGeneratingDocuments, setIsGeneratingDocuments] = useState(false); // NOWA FLAGA

    const isMobile = !screens.md;

    // Replace all message calls with messageApi
    const showMessage = {
        success: (content) => messageApi.success(content),
        error: (content) => messageApi.error(content),
        loading: (content, duration) => messageApi.loading(content, duration),
        destroy: () => messageApi.destroy()
    };

    useEffect(() => {
        if (userStatus === 'Oczekuje na weryfikacje' && isModalVisible) {
            startCountdown();
        }
    }, [userStatus, isModalVisible]);

    // ZMODYFIKOWANY useEffect do nasłuchiwania zmian statusu
    useEffect(() => {
        let unsubscribe;
        let documentGenerationHandled = false; // Lokalna flaga

        if (currentUser) {
            const userDocRef = doc(db, 'users', currentUser.uid);

            const checkCarOwnership = async (status) => {
                if (status === 'Dane dodatkowe') {
                    try {
                        const carDocRef = doc(db, `users/${currentUser.uid}/dokumenty`, 'samochod');
                        const carDoc = await getDoc(carDocRef);
                        if (carDoc.exists() && carDoc.data().carOwnership) {
                            setCurrentStep(2); // Skip to documents step
                            setCarOwnership(carDoc.data().carOwnership === 'Partnera' ? 'partner' : 'own');
                        } else {
                            setCurrentStep(1); // Show car choice
                        }
                    } catch (error) {
                        console.error('Error fetching car data:', error);
                        setCurrentStep(1); // Default to car choice
                    }
                }
            };

            // NOWA FUNKCJA do generowania dokumentów
            const handleDocumentGeneration = async (userId) => {
                if (documentGenerationHandled || isGeneratingDocuments) return;

                documentGenerationHandled = true;
                setIsGeneratingDocuments(true);

                try {
                    console.log('Rozpoczynam generowanie dokumentów...');

                    // Sprawdź czy dokumenty już istnieją
                    const existingDocs = await getDocs(
                        collection(db, `users/${userId}/umowy`)
                    );

                    if (existingDocs.empty) {
                        console.log('Brak istniejących dokumentów, generuję nowe...');

                        // Generuj dokumenty
                        const success = await generateDocumentsForUser(userId);

                        if (success) {
                            console.log('Dokumenty wygenerowane pomyślnie');

                            // Zmień status na "Oczekuje na podpis dokumentów"
                            await setDoc(
                                doc(db, 'users', userId),
                                { status: 'Oczekuje na podpis dokumentów' },
                                { merge: true }
                            );
                        } else {
                            console.error('Nie udało się wygenerować dokumentów');
                            showMessage.error('Błąd podczas generowania dokumentów');

                            // Przywróć poprzedni status
                            await setDoc(
                                doc(db, 'users', userId),
                                { status: 'Dane dodatkowe' },
                                { merge: true }
                            );
                        }
                    } else {
                        console.log('Dokumenty już istnieją, zmieniam status');
                        // Dokumenty już istnieją, zmień status
                        await setDoc(
                            doc(db, 'users', userId),
                            { status: 'Oczekuje na podpis dokumentów' },
                            { merge: true }
                        );
                    }
                } catch (error) {
                    console.error('Błąd podczas generowania dokumentów:', error);
                    showMessage.error('Wystąpił błąd podczas generowania dokumentów');

                    // Przywróć poprzedni status w razie błędu
                    try {
                        await setDoc(
                            doc(db, 'users', userId),
                            { status: 'Dane dodatkowe' },
                            { merge: true }
                        );
                    } catch (e) {
                        console.error('Błąd podczas przywracania statusu:', e);
                    }
                } finally {
                    setIsGeneratingDocuments(false);
                }
            };

            unsubscribe = onSnapshot(userDocRef, async (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const status = docSnapshot.data().status;
                    setUserStatus(status);

                    // NOWA LOGIKA - generowanie dokumentów
                    if (status === 'Wymagana weryfikacja') {
                        setIsModalVisible(true);
                        await handleDocumentGeneration(currentUser.uid);
                    } else if (status === 'Oczekuje na podpis dokumentów') {
                        setIsModalVisible(true);
                    } else if (status === 'Oczekuje na weryfikacje') {
                        setIsModalVisible(true);
                    } else if (status === 'Dane dodatkowe') {
                        checkCarOwnership(status);
                        setIsModalVisible(true);
                    } else if (status === 'Wymagana weryfikacja KYC') {
                        setCurrentStep(0);
                        setIsModalVisible(true);
                    }
                }
            });
        }
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [currentUser, isGeneratingDocuments]);

    const intervalIdRef = useRef(null);

    const startCountdown = () => {
        intervalIdRef.current = setInterval(() => {
            setCountdown(prevCountdown => {
                if (prevCountdown <= 1) {
                    clearInterval(intervalIdRef.current);
                    handleLogout();
                    return 0;
                }
                return prevCountdown - 1;
            });
        }, 1000);
    };

    useEffect(() => {
        return () => {
            if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
            }
        };
    }, []);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            setIsModalVisible(false);
            showMessage.success('Pomyślnie się wylogowałeś.');
        } catch (error) {
            showMessage.error('Błąd podczas wylogowywania.');
        }
    };

    useEffect(() => {
        const fetchUserStatus = async () => {
            if (currentUser) {
                const userDocRef = doc(db, 'users', currentUser.uid);
                const userDoc = await getDoc(userDocRef);

                if (userDoc.exists()) {
                    const status = userDoc.data().status;
                    setUserStatus(status);

                    if (status === 'Dane dodatkowe') {
                        // Check if car choice was already made
                        try {
                            const carDocRef = doc(db, `users/${currentUser.uid}/dokumenty`, 'samochod');
                            const carDoc = await getDoc(carDocRef);
                            if (carDoc.exists() && carDoc.data().carOwnership) {
                                setCurrentStep(2); // Skip to documents step
                                setCarOwnership(carDoc.data().carOwnership === 'Partnera' ? 'partner' : 'own');
                            } else {
                                setCurrentStep(1); // Show car choice
                            }
                        } catch (error) {
                            console.error('Error fetching car data:', error);
                            setCurrentStep(1); // Default to car choice
                        }
                    } else if (status === 'Wymagana weryfikacja KYC') {
                        setCurrentStep(0);
                    }
                }
            }
            setLoading(false);
        };

        const fetchUrzedy = async () => {
            try {
                const urzedyCollection = collection(db, 'urzedy_skarbowe');
                const urzedySnapshot = await getDocs(urzedyCollection);
                const urzedyList = urzedySnapshot.docs.map(doc => ({ value: doc.data().nazwa }));
                setUrzedyOptions(urzedyList);
            } catch (error) {
                console.error('Error fetching urzedy:', error);
            }
        };

        if (currentUser) {
            fetchUserStatus();
            fetchUrzedy();
        }
    }, [currentUser]);

    useEffect(() => {
        if (userStatus === 'Oczekuje na podpis dokumentów') {
            setIsModalVisible(true);
        }
    }, [userStatus]);

    useEffect(() => {
        if (userStatus) {
            setIsModalVisible(true);
        }
    }, [userStatus]);

    useEffect(() => {
        if (isSumsubModalVisible && !isSdkInitialized) {
            initializeSumsubSDK();
        }
    }, [isSumsubModalVisible, isSdkInitialized]);

    const initializeSumsubSDK = async () => {
        if (window.snsWebSdk && currentUser) {
            try {
                setIsLoadingSumsub(true);
                const accessToken = await getAccessToken(currentUser.uid);
                startSumsubSDK(accessToken);
            } catch (error) {
                console.error('Error during initialization:', error);
                setLoadingMessage(error.message);
            } finally {
                setIsLoadingSumsub(false);
            }
        } else {
            console.error('Sumsub SDK nie załadowany');
            setLoadingMessage('Sumsub SDK nie załadowany');
        }
    };

    const getAccessToken = async (userId) => {
        const callable = httpsCallable(functions, 'generateSumsubAccessToken');
        const result = await callable({ userId });
        if (!result.data || !result.data.accessToken) {
            throw new Error('No accessToken returned from generateSumsubAccessToken');
        }
        return result.data.accessToken;
    };

    const startSumsubSDK = (accessToken) => {
        const container = document.getElementById('sumsub-container');
        container.style.display = 'block';
        setLoadingMessage('');

        let snsWebSdkInstance = window.snsWebSdk
            .init(accessToken, () => getAccessToken(currentUser.uid))
            .withConf({
                lang: 'pl',
                theme: 'light',
                predefinedCountry: 'POL',
            })
            .withOptions({ addViewportTag: false, adaptIframeHeight: true })
            .on('idCheck.onApplicantStatusChanged', async (payload) => {
                console.log('Applicant Status Changed:', payload);

                if (payload.reviewStatus === 'completed' || payload.reviewStatus === 'approved') {
                    await setDoc(doc(db, 'users', currentUser.uid), {
                        status: 'Dane dodatkowe',
                    }, { merge: true });

                    setUserStatus('Dane dodatkowe');
                    setCurrentStep(1); // Go to car choice step

                    setIsSumsubModalVisible(false);
                    setIsSdkInitialized(false);
                }
            })
            .on('idCheck.onError', (error) => {
                console.error('onError', error);
                setLoadingMessage('Error during verification.');
                showMessage.error('Wystąpił błąd podczas weryfikacji.');
            })
            .build();

        snsWebSdkInstance.launch('#sumsub-container');
        setIsSdkInitialized(true);
    };

    const handleCarOwnershipChange = e => {
        const value = e.target.value;
        setCarOwnership(value);
    };

    const handleNextStep = async () => {
        try {
            // Special handling for partner car selection
            if (currentStep === 1 && carOwnership === 'partner') {
                setIsSavingData(true);
                const loadingKey = 'loading';
                showMessage.loading({ content: 'Zapisywanie wyboru...', key: loadingKey, duration: 0 });

                try {
                    await setDoc(doc(db, `users/${currentUser.uid}/dokumenty`, 'samochod'), {
                        carOwnership: 'Partnera',
                    });

                    showMessage.success({ content: 'Wybór pojazdu partnera został zapisany!', key: loadingKey });

                    setTimeout(() => {
                        setCurrentStep(2);
                        form.resetFields();
                    }, 500);

                    return;
                } catch (error) {
                    console.error('Błąd podczas zapisywania wyboru pojazdu partnera:', error);
                    showMessage.error('Wystąpił błąd podczas zapisywania wyboru.');
                    setIsSavingData(false);
                    return;
                }
            }

            // Normal validation and save for other steps
            await form.validateFields();
            const values = form.getFieldsValue();

            setIsSavingData(true);
            const loadingKey = 'loading';
            showMessage.loading({ content: 'Proszę czekać, trwa zapisywanie...', key: loadingKey, duration: 0 });

            if (currentStep === 1) {
                await saveCarData(values);
            } else if (currentStep === 2) {
                await saveAdditionalData(values);
            } else if (currentStep === 3) {
                await savePersonalData(values);
            }

            showMessage.success({ content: 'Dane zapisane pomyślnie!', key: loadingKey });

            if (currentStep < 3) {
                setTimeout(() => {
                    setCurrentStep(prevStep => prevStep + 1);
                    form.resetFields();
                    setUploadProgress({});
                    setUploadedFiles({});
                    setSelectedFiles({});
                }, 500);
            } else {
                handleComplete();
            }
        } catch (error) {
            console.error('Validation failed:', error);
            showMessage.error('Proszę uzupełnić wszystkie pola przed przejściem dalej.');
        } finally {
            setIsSavingData(false);
        }
    };

    const handleComplete = async () => {
        try {
            await setDoc(doc(db, 'users', currentUser.uid), { status: 'Oczekuje na weryfikacje' }, { merge: true });
            setIsModalVisible(true);
        } catch (error) {
            showMessage.error('Wystąpił błąd podczas aktualizacji statusu.');
        }
    };

    const uploadFile = async (file, fileName) => {
        const storageRef = ref(storage, `users/${currentUser.uid}/${fileName}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        return new Promise((resolve, reject) => {
            uploadTask.on(
                'state_changed',
                snapshot => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(prev => ({
                        ...prev,
                        [fileName]: progress
                    }));
                },
                error => {
                    console.error('Upload failed:', error);
                    showMessage.error('Wystąpił błąd podczas przesyłania pliku.');
                    reject(error);
                },
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then(downloadURL => {
                        setUploadProgress(prev => ({
                            ...prev,
                            [fileName]: 100
                        }));
                        setUploadedFiles(prev => ({
                            ...prev,
                            [fileName]: true
                        }));
                        resolve(downloadURL);
                    });
                }
            );
        });
    };

    const saveCarData = async (values) => {
        try {
            const carData = carOwnership === 'partner' ? {
                carOwnership: 'Partnera',
            } : {
                carOwnership: 'Własny',
                carMake: values.brand,
                carModel: values.model,
                carYear: values.year,
                carVin: values.vin,
                carRegistrationNumber: values.registrationPlates,
                dowodPrzod: await uploadFile(values.dowodPrzod.file.originFileObj, 'dowodPrzod'),
                dowodTyl: await uploadFile(values.dowodTyl.file.originFileObj, 'dowodTyl'),
                ubezpieczenie: await uploadFile(values.ubezpieczenie.file.originFileObj, 'ubezpieczenie'),
            };

            await setDoc(doc(db, `users/${currentUser.uid}/dokumenty`, 'samochod'), carData);
        } catch (error) {
            showMessage.error('Wystąpił błąd podczas zapisywania danych pojazdu.');
            throw error;
        }
    };

    const saveAdditionalData = async (values) => {
        try {
            const additionalData = {};

            additionalData.niekaralnosc = await uploadFile(values.niekaralnosc.file.originFileObj, 'niekaralnosc');
            additionalData.badaniaLekarskie = await uploadFile(values.badaniaLekarskie.file.originFileObj, 'badaniaLekarskie');
            additionalData.badaniaPsychologiczne = await uploadFile(values.badaniaPsychologiczne.file.originFileObj, 'badaniaPsychologiczne');
            additionalData.dowodOsobistyPrzod = await uploadFile(values.dowodOsobistyPrzod.file.originFileObj, 'dowodOsobistyPrzod');
            additionalData.dowodOsobistyTyl = await uploadFile(values.dowodOsobistyTyl.file.originFileObj, 'dowodOsobistyTyl');
            additionalData.zdjecieProfilowe = await uploadFile(values.zdjecieProfilowe.file.originFileObj, 'zdjecieProfilowe');

            await setDoc(doc(db, `users/${currentUser.uid}/dokumenty`, 'dane_dodatkowe'), additionalData);
        } catch (error) {
            showMessage.error('Wystąpił błąd podczas zapisywania dodatkowych danych.');
            throw error;
        }
    };

    const formatAddress = (street, houseNumber, city, postalCode) => {
        return `${street} ${houseNumber}, ${city}, ${postalCode}`;
    };

    const savePersonalData = async (values) => {
        try {
            const formattedAddress = formatAddress(values.street, values.houseNumber, values.city, values.postalCode);
            const personalData = {
                pesel: values.pesel,
                city: values.city,
                postalCode: values.postalCode,
                street: `${values.street} ${values.houseNumber}`,
                correspondenceAddress: formattedAddress,
                registrationAddress: formattedAddress,
                residenceAddress: formattedAddress,
                citizenship: values.citizenship,
            };

            await setDoc(doc(db, `users/${currentUser.uid}/dokumenty`, 'dane_personalne'), personalData);
            await setDoc(doc(db, 'users', currentUser.uid), { citizenship: values.citizenship }, { merge: true });
            await setDoc(doc(db, `users/${currentUser.uid}/dokumenty`, 'samochod'), { citizenship: values.citizenship }, { merge: true });

            const danePodatkowe = {
                rachunekBankowy: values.bankAccount,
                urzadSkarbowy: values.taxOffice,
                settlementPeriod: values.settlementPeriod,
            };

            await setDoc(doc(db, `users/${currentUser.uid}/dokumenty`, 'dane_podatkowe'), danePodatkowe);

            showMessage.success('Dane osobowe i podatkowe zapisane pomyślnie.');
        } catch (error) {
            showMessage.error('Wystąpił błąd podczas zapisywania danych osobowych.');
            throw error;
        }
    };

    const getCurrentStep = () => {
        switch (userStatus) {
            case 'Wymagana weryfikacja KYC':
                return 0;
            case 'Dane dodatkowe':
                return currentStep; // Use the actual currentStep value
            default:
                return 0;
        }
    };

    const uploadProps = {
        beforeUpload: (file) => {
            const isValid = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'application/pdf';
            if (!isValid) {
                showMessage.error('Możesz przesłać tylko pliki JPG/PNG/PDF!');
            }
            const isLt5M = file.size / 1024 / 1024 < 5;
            if (!isLt5M) {
                showMessage.error('Plik musi być mniejszy niż 5MB!');
            }
            return isValid && isLt5M;
        },
        maxCount: 1,
        showUploadList: false
    };

    const MobileUploadButton = ({ icon, title, fieldName, isUploaded, selectedFile }) => (
        <div style={{ marginBottom: '16px' }}>
            <Text type="secondary" style={{ fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                {title}
            </Text>
            <div style={{
                ...mobileStyles.uploadButton,
                borderColor: isUploaded ? '#52c41a' : (selectedFile ? '#1890ff' : '#d9d9d9'),
                backgroundColor: isUploaded ? '#f6ffed' : (selectedFile ? '#e6f7ff' : '#fafafa')
            }}>
                {isUploaded ? (
                    <>
                        <CheckCircleOutlined style={{ fontSize: '32px', color: '#52c41a' }} />
                        <Text style={{ color: '#52c41a', fontWeight: '500' }}>Przesłano</Text>
                    </>
                ) : selectedFile ? (
                    <>
                        <FileOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
                        <Text style={{
                            color: '#1890ff',
                            fontWeight: '500',
                            fontSize: '14px',
                            maxWidth: '90%',
                            minWidth: '90vh',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                            display: 'block'
                        }}>
                            {selectedFile.name}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </Text>
                    </>
                ) : (
                    <>
                        {icon}
                        <Text>Dotknij aby przesłać</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>JPG, PNG lub PDF (max 5MB)</Text>
                    </>
                )}
                {uploadProgress[fieldName] > 0 && uploadProgress[fieldName] < 100 && (
                    <Progress
                        percent={Math.round(uploadProgress[fieldName])}
                        size="small"
                        style={{ width: '80%', marginTop: '8px' }}
                    />
                )}
            </div>
        </div>
    );

    const handleOpenSumsub = () => {
        // Закрываем основное модальное окно
        setIsModalVisible(false);
        // Открываем Sumsub после небольшой задержки
        requestAnimationFrame(() => {
            setIsSumsubModalVisible(true);
        });
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <div style={{ padding: '24px', textAlign: 'center' }}>
                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            backgroundColor: '#e6f7ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 24px'
                        }}>
                            <UserOutlined style={{ fontSize: 40, color: '#1890ff' }} />
                        </div>
                        <Title level={3} style={{ marginBottom: '8px' }}>Weryfikacja KYC</Title>
                        <Paragraph style={{ fontSize: '16px', color: '#666', marginBottom: '32px' }}>
                            Zweryfikuj swoją tożsamość, aby rozpocząć współpracę. Proces zajmie tylko kilka minut.
                        </Paragraph>
                        <Button
                            type="primary"
                            size="large"
                            block
                            onClick={handleOpenSumsub}
                            style={mobileStyles.button}
                        >
                            Rozpocznij weryfikację
                        </Button>
                    </div>
                );

            case 1:
                return (
                    <div style={{ padding: '16px' }}>
                        <Title level={4} style={{ marginBottom: '20px' }}>
                            <CarOutlined /> Wybór pojazdu
                        </Title>

                        <Radio.Group
                            onChange={handleCarOwnershipChange}
                            value={carOwnership}
                            style={{ width: '100%' }}
                        >
                            <div
                                onClick={() => setCarOwnership('own')}
                                style={{
                                    ...mobileStyles.radioCard,
                                    borderColor: carOwnership === 'own' ? '#1890ff' : '#f0f0f0',
                                    backgroundColor: carOwnership === 'own' ? '#e6f7ff' : '#fff'
                                }}
                            >
                                <Radio value="own" style={{ width: '100%' }}>
                                    <div style={{ marginLeft: '8px' }}>
                                        <Text strong style={{ fontSize: '16px' }}>Własny pojazd</Text>
                                        <br />
                                        <Text type="secondary" style={{ fontSize: '14px' }}>
                                            Będę korzystać z własnego samochodu
                                        </Text>
                                    </div>
                                </Radio>
                            </div>

                            <div
                                onClick={() => setCarOwnership('partner')}
                                style={{
                                    ...mobileStyles.radioCard,
                                    borderColor: carOwnership === 'partner' ? '#1890ff' : '#f0f0f0',
                                    backgroundColor: carOwnership === 'partner' ? '#e6f7ff' : '#fff'
                                }}
                            >
                                <Radio value="partner" style={{ width: '100%' }}>
                                    <div style={{ marginLeft: '8px' }}>
                                        <Text strong style={{ fontSize: '16px' }}>Pojazd partnera</Text>
                                        <br />
                                        <Text type="secondary" style={{ fontSize: '14px' }}>
                                            Będę korzystać z pojazdu udostępnionego przez partnera
                                        </Text>
                                    </div>
                                </Radio>
                            </div>
                        </Radio.Group>

                        {carOwnership === 'own' && (
                            <Form form={form} layout="vertical" style={{ marginTop: '24px' }}>
                                <Title level={5} style={{ marginBottom: '16px' }}>Dane pojazdu</Title>

                                <Form.Item label="Marka" name="brand" rules={[{ required: true, message: 'Wymagane' }]}>
                                    <Input placeholder="np. Toyota" style={mobileStyles.input} />
                                </Form.Item>

                                <Form.Item label="Model" name="model" rules={[{ required: true, message: 'Wymagane' }]}>
                                    <Input placeholder="np. Corolla" style={mobileStyles.input} />
                                </Form.Item>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item label="Rok" name="year" rules={[{ required: true, message: 'Wymagane' }]}>
                                            <Input placeholder="np. 2020" style={mobileStyles.input} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label="Tablice" name="registrationPlates" rules={[{ required: true, message: 'Wymagane' }]}>
                                            <Input placeholder="np. WA12345" style={mobileStyles.input} />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item label="VIN" name="vin" rules={[{ required: true, message: 'Wymagane' }]}>
                                    <Input placeholder="17-znakowy numer VIN" style={mobileStyles.input} />
                                </Form.Item>

                                <Divider>Dokumenty pojazdu</Divider>

                                <Form.Item name="dowodPrzod" rules={[{ required: true, message: 'Wymagane' }]}>
                                    <Upload {...uploadProps} onChange={(info) => {
                                        if (info.file) {
                                            setSelectedFiles(prev => ({
                                                ...prev,
                                                dowodPrzod: {
                                                    name: info.file.name,
                                                    size: info.file.size
                                                }
                                            }));
                                        }
                                    }}>
                                        <MobileUploadButton
                                            icon={<FileImageOutlined style={{ fontSize: '32px', color: '#1890ff' }} />}
                                            title="Dowód rejestracyjny - przód"
                                            fieldName="dowodPrzod"
                                            isUploaded={uploadedFiles.dowodPrzod}
                                            selectedFile={selectedFiles.dowodPrzod}
                                        />
                                    </Upload>
                                </Form.Item>

                                <Form.Item name="dowodTyl" rules={[{ required: true, message: 'Wymagane' }]}>
                                    <Upload {...uploadProps} onChange={(info) => {
                                        if (info.file) {
                                            setSelectedFiles(prev => ({
                                                ...prev,
                                                dowodTyl: {
                                                    name: info.file.name,
                                                    size: info.file.size
                                                }
                                            }));
                                        }
                                    }}>
                                        <MobileUploadButton
                                            icon={<FileImageOutlined style={{ fontSize: '32px', color: '#1890ff' }} />}
                                            title="Dowód rejestracyjny - tył"
                                            fieldName="dowodTyl"
                                            isUploaded={uploadedFiles.dowodTyl}
                                            selectedFile={selectedFiles.dowodTyl}
                                        />
                                    </Upload>
                                </Form.Item>

                                <Form.Item name="ubezpieczenie" rules={[{ required: true, message: 'Wymagane' }]}>
                                    <Upload {...uploadProps} onChange={(info) => {
                                        if (info.file) {
                                            setSelectedFiles(prev => ({
                                                ...prev,
                                                ubezpieczenie: {
                                                    name: info.file.name,
                                                    size: info.file.size
                                                }
                                            }));
                                        }
                                    }}>
                                        <MobileUploadButton
                                            icon={<SafetyOutlined style={{ fontSize: '32px', color: '#1890ff' }} />}
                                            title="Polisa ubezpieczeniowa"
                                            fieldName="ubezpieczenie"
                                            isUploaded={uploadedFiles.ubezpieczenie}
                                            selectedFile={selectedFiles.ubezpieczenie}
                                        />
                                    </Upload>
                                </Form.Item>

                                <Button
                                    type="primary"
                                    onClick={handleNextStep}
                                    loading={isSavingData}
                                    block
                                    size="large"
                                    style={mobileStyles.button}
                                >
                                    Dalej
                                </Button>
                            </Form>
                        )}

                        {carOwnership && (
                            <Button
                                type="primary"
                                onClick={handleNextStep}
                                loading={isSavingData}
                                block
                                size="large"
                                style={mobileStyles.button}
                            >
                                Dalej
                            </Button>
                        )}

                        {carOwnership === 'partner' && (
                            <div style={{ marginTop: '24px' }}>
                                <Alert
                                    message="Pojazd partnera"
                                    description="Wybrałeś opcję korzystania z pojazdu partnera. Nie musisz przesyłać dokumentów pojazdu."
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: '24px' }}
                                />
                                <Button
                                    type="primary"
                                    onClick={handleNextStep}
                                    loading={isSavingData}
                                    block
                                    size="large"
                                    style={mobileStyles.button}
                                    icon={<RightOutlined />}
                                >
                                    Przejdź dalej
                                </Button>
                            </div>
                        )}
                    </div>
                );

            case 2:
                return (
                    <div style={{ padding: '16px' }}>
                        <Title level={4} style={{ marginBottom: '20px' }}>
                            <FileTextOutlined /> Dokumenty
                        </Title>

                        <Alert
                            message="Przesyłaj tylko aktualne dokumenty"
                            type="info"
                            showIcon
                            style={{ marginBottom: '20px' }}
                        />

                        <Form form={form} layout="vertical">
                            <Form.Item name="niekaralnosc" rules={[{ required: true, message: 'Wymagane' }]}>
                                <Upload {...uploadProps} onChange={(info) => {
                                    if (info.file) {
                                        setSelectedFiles(prev => ({
                                            ...prev,
                                            niekaralnosc: { name: info.file.name, size: info.file.size }
                                        }));
                                    }
                                }}>
                                    <MobileUploadButton
                                        icon={<SafetyOutlined style={{ fontSize: '32px', color: '#1890ff' }} />}
                                        title="Zaświadczenie o niekaralności"
                                        fieldName="niekaralnosc"
                                        isUploaded={uploadedFiles.niekaralnosc}
                                        selectedFile={selectedFiles.niekaralnosc}
                                    />
                                </Upload>
                            </Form.Item>

                            <Form.Item name="badaniaLekarskie" rules={[{ required: true, message: 'Wymagane' }]}>
                                <Upload {...uploadProps} onChange={(info) => {
                                    if (info.file) {
                                        setSelectedFiles(prev => ({
                                            ...prev,
                                            badaniaLekarskie: { name: info.file.name, size: info.file.size }
                                        }));
                                    }
                                }}>
                                    <MobileUploadButton
                                        icon={<MedicineBoxOutlined style={{ fontSize: '32px', color: '#1890ff' }} />}
                                        title="Badania lekarskie"
                                        fieldName="badaniaLekarskie"
                                        isUploaded={uploadedFiles.badaniaLekarskie}
                                        selectedFile={selectedFiles.badaniaLekarskie}
                                    />
                                </Upload>
                            </Form.Item>

                            <Form.Item name="badaniaPsychologiczne" rules={[{ required: true, message: 'Wymagane' }]}>
                                <Upload {...uploadProps} onChange={(info) => {
                                    if (info.file) {
                                        setSelectedFiles(prev => ({
                                            ...prev,
                                            badaniaPsychologiczne: { name: info.file.name, size: info.file.size }
                                        }));
                                    }
                                }}>
                                    <MobileUploadButton
                                        icon={<SolutionOutlined style={{ fontSize: '32px', color: '#1890ff' }} />}
                                        title="Badania psychologiczne"
                                        fieldName="badaniaPsychologiczne"
                                        isUploaded={uploadedFiles.badaniaPsychologiczne}
                                        selectedFile={selectedFiles.badaniaPsychologiczne}
                                    />
                                </Upload>
                            </Form.Item>

                            <Form.Item name="zdjecieProfilowe" rules={[{ required: true, message: 'Wymagane' }]}>
                                <Upload {...uploadProps} onChange={(info) => {
                                    if (info.file) {
                                        setSelectedFiles(prev => ({
                                            ...prev,
                                            zdjecieProfilowe: { name: info.file.name, size: info.file.size }
                                        }));
                                    }
                                }}>
                                    <MobileUploadButton
                                        icon={<CameraOutlined style={{ fontSize: '32px', color: '#1890ff' }} />}
                                        title="Zdjęcie profilowe"
                                        fieldName="zdjecieProfilowe"
                                        isUploaded={uploadedFiles.zdjecieProfilowe}
                                        selectedFile={selectedFiles.zdjecieProfilowe}
                                    />
                                </Upload>
                            </Form.Item>

                            <Divider>Dowód osobisty</Divider>

                            <Form.Item name="dowodOsobistyPrzod" rules={[{ required: true, message: 'Wymagane' }]}>
                                <Upload {...uploadProps} onChange={(info) => {
                                    if (info.file) {
                                        setSelectedFiles(prev => ({
                                            ...prev,
                                            dowodOsobistyPrzod: { name: info.file.name, size: info.file.size }
                                        }));
                                    }
                                }}>
                                    <MobileUploadButton
                                        icon={<IdcardOutlined style={{ fontSize: '32px', color: '#1890ff' }} />}
                                        title="Dowód osobisty - przód"
                                        fieldName="dowodOsobistyPrzod"
                                        isUploaded={uploadedFiles.dowodOsobistyPrzod}
                                        selectedFile={selectedFiles.dowodOsobistyPrzod}
                                    />
                                </Upload>
                            </Form.Item>

                            <Form.Item name="dowodOsobistyTyl" rules={[{ required: true, message: 'Wymagane' }]}>
                                <Upload {...uploadProps} onChange={(info) => {
                                    if (info.file) {
                                        setSelectedFiles(prev => ({
                                            ...prev,
                                            dowodOsobistyTyl: { name: info.file.name, size: info.file.size }
                                        }));
                                    }
                                }}>
                                    <MobileUploadButton
                                        icon={<IdcardOutlined style={{ fontSize: '32px', color: '#1890ff' }} />}
                                        title="Dowód osobisty - tył"
                                        fieldName="dowodOsobistyTyl"
                                        isUploaded={uploadedFiles.dowodOsobistyTyl}
                                        selectedFile={selectedFiles.dowodOsobistyTyl}
                                    />
                                </Upload>
                            </Form.Item>

                            <Button
                                type="primary"
                                onClick={handleNextStep}
                                loading={isSavingData}
                                block
                                size="large"
                                style={mobileStyles.button}
                            >
                                Dalej
                            </Button>
                        </Form>
                    </div>
                );

            case 3:
                return (
                    <div style={{ padding: '16px' }}>
                        <Title level={4} style={{ marginBottom: '20px' }}>
                            <IdcardOutlined /> Dane osobowe
                        </Title>

                        <Form form={form} layout="vertical">
                            <Card style={mobileStyles.card}>
                                <Title level={5} style={{ marginBottom: '16px' }}>
                                    <BankOutlined /> Dane rozliczeniowe
                                </Title>

                                <Form.Item
                                    label="Konto bankowe"
                                    name="bankAccount"
                                    rules={[
                                        { required: true, message: 'Wymagane' },
                                        { len: 26, message: 'Musi zawierać 26 cyfr' },
                                        { pattern: /^[0-9]{26}$/, message: 'Tylko cyfry' }
                                    ]}
                                >
                                    <Input
                                        placeholder="26 cyfr numeru konta"
                                        maxLength={26}
                                        showCount
                                        style={mobileStyles.input}
                                    />
                                </Form.Item>

                                <Form.Item
                                    label="Urząd skarbowy"
                                    name="taxOffice"
                                    rules={[{ required: true, message: 'Wymagane' }]}
                                >
                                    <AutoComplete
                                        options={urzedyOptions}
                                        placeholder="Wybierz urząd"
                                        filterOption={(inputValue, option) =>
                                            option.value.toLowerCase().includes(inputValue.toLowerCase())
                                        }
                                        style={{ height: '48px' }}
                                    />
                                </Form.Item>

                                <Form.Item
                                    label="Okres rozliczeniowy"
                                    name="settlementPeriod"
                                    rules={[{ required: true, message: 'Wymagane' }]}
                                >
                                    <Segmented
                                        options={[
                                            { label: 'Tydzień', value: 'weekly' },
                                            { label: '2 tygodnie', value: 'biweekly' },
                                            { label: 'Miesiąc', value: 'monthly' }
                                        ]}
                                        block
                                        style={{ fontSize: '16px' }}
                                    />
                                </Form.Item>
                            </Card>

                            <Card style={mobileStyles.card}>
                                <Title level={5} style={{ marginBottom: '16px' }}>
                                    <UserOutlined /> Dane personalne
                                </Title>

                                <Form.Item
                                    label="PESEL"
                                    name="pesel"
                                    rules={[
                                        { required: true, message: 'Wymagane' },
                                        { len: 11, message: 'Musi zawierać 11 cyfr' },
                                        { pattern: /^[0-9]{11}$/, message: 'Tylko cyfry' }
                                    ]}
                                >
                                    <Input
                                        placeholder="11 cyfr PESEL"
                                        maxLength={11}
                                        showCount
                                        style={mobileStyles.input}
                                    />
                                </Form.Item>

                                <Form.Item
                                    label="Obywatelstwo"
                                    name="citizenship"
                                    rules={[{ required: true, message: 'Wymagane' }]}
                                >
                                    <Input placeholder="np. Polska" style={mobileStyles.input} />
                                </Form.Item>
                            </Card>

                            <Card style={mobileStyles.card}>
                                <Title level={5} style={{ marginBottom: '16px' }}>
                                    <HomeOutlined /> Adres zamieszkania
                                </Title>

                                <Form.Item
                                    label="Ulica"
                                    name="street"
                                    rules={[{ required: true, message: 'Wymagane' }]}
                                >
                                    <Input placeholder="Nazwa ulicy" style={mobileStyles.input} />
                                </Form.Item>

                                <Form.Item
                                    label="Numer domu/mieszkanie"
                                    name="houseNumber"
                                    rules={[{ required: true, message: 'Wymagane' }]}
                                >
                                    <Input placeholder="np. 12/5" style={mobileStyles.input} />
                                </Form.Item>

                                <Form.Item
                                    label="Miasto"
                                    name="city"
                                    rules={[{ required: true, message: 'Wymagane' }]}
                                >
                                    <Input placeholder="Nazwa miasta" style={mobileStyles.input} />
                                </Form.Item>

                                <Form.Item
                                    label="Kod pocztowy"
                                    name="postalCode"
                                    rules={[
                                        { required: true, message: 'Wymagane' },
                                        {
                                            pattern: /^[0-9]{2}-[0-9]{3}$/,
                                            message: 'Format: 01-111',
                                        },
                                    ]}
                                >
                                    <Input placeholder="00-000" maxLength={6} style={mobileStyles.input} />
                                </Form.Item>
                            </Card>

                            <Button
                                type="primary"
                                onClick={handleNextStep}
                                loading={isSavingData}
                                block
                                size="large"
                                style={mobileStyles.button}
                                icon={<CheckCircleOutlined />}
                            >
                                Zakończ rejestrację
                            </Button>
                        </Form>
                    </div>
                );

            default:
                return null;
        }
    };

    const stepTitles = ['Weryfikacja', 'Pojazd', 'Dokumenty', 'Dane'];

    return (
        <UserStatusContext.Provider value={{ userStatus }}>
            {contextHolder}
            {!loading && children}
            {(userStatus === 'Wymagana weryfikacja KYC' || userStatus === 'Dane dodatkowe') && isModalVisible && !isSumsubModalVisible && (
                isMobile ? (
                    <Drawer
                        open={true}
                        placement="bottom"
                        height="100vh"
                        closable={false}
                        maskClosable={false}
                        bodyStyle={{
                            padding: 0,
                            overflow: 'hidden',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        style={{ borderRadius: '16px 16px 0 0' }}
                        zIndex={1000}
                        maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
                    >
                        {/* Фиксированный хедер со Steps */}
                        <div style={{
                            position: 'sticky',
                            top: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: '#fff',
                            zIndex: 10,
                            padding: '12px 16px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            minHeight: '86px',
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0
                        }}>
                            <div style={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px'
                            }}>
                                <Text strong style={{ fontSize: '14px', textAlign: 'center' }}>
                                    {stepTitles[currentStep]} ({currentStep + 1}/{stepTitles.length})
                                </Text>
                                <Steps
                                    size="small"
                                    current={currentStep}
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between'
                                    }}
                                    responsive={false}
                                >
                                    {stepTitles.map((title, index) => (
                                        <Step
                                            key={index}
                                            title=""
                                        />
                                    ))}
                                </Steps>
                                <Progress
                                    percent={Math.round(((currentStep + 1) / stepTitles.length) * 100)}
                                    showInfo={false}
                                    strokeColor="#1890ff"
                                    style={{ margin: '0 -8px' }}
                                />
                            </div>
                        </div>

                        {/* Скроллируемый контент */}
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            WebkitOverflowScrolling: 'touch',
                            paddingBottom: '32px',
                            position: 'relative'
                        }}>
                            {renderStepContent()}
                        </div>
                    </Drawer>
                ) : (
                    <Modal
                        open={true}
                        closable={false}
                        footer={null}
                        maskClosable={false}
                        keyboard={false}
                        width={800}
                        styles={{
                            body: { padding: '24px 40px' }  // Zmienione z bodyStyle
                        }}
                    >
                        <Steps
                            size="small"
                            current={currentStep}
                            style={{ marginBottom: '32px' }}
                            responsive={false}
                        >
                            <Step title="Weryfikacja KYC" icon={<UserOutlined />} />
                            <Step title="Dane pojazdu" icon={<CarOutlined />} />
                            <Step title="Dokumenty" icon={<FileTextOutlined />} />
                            <Step title="Dane osobowe" icon={<IdcardOutlined />} />
                        </Steps>

                        {renderStepContent()}
                    </Modal>
                )
            )}
            {isSuccess && (
                <Modal
                    open={true}
                    closable={false}
                    footer={null}
                    maskClosable={false}
                    keyboard={false}
                    width={isMobile ? '100%' : 600}
                >
                    <Result
                        icon={<Lottie
                            animationData={successAnimation}
                            loop={false}
                            autoplay
                            style={{ height: 200, width: 200 }}
                        />}
                        title="Wszystkie dane zapisane pomyślnie!"
                        subTitle="Oczekuj na zatwierdzenie przez administratora."
                    />
                </Modal>
            )}
            {userStatus === 'Oczekuje na weryfikacje' && isModalVisible && (
                <Modal
                    open={true}
                    closable={false}
                    footer={null}
                    maskClosable={false}
                    keyboard={false}
                    width={isMobile ? '100%' : 600}
                >
                    <Result
                        icon={<Lottie
                            animationData={awaitingApprovalAnimation}
                            loop={true}
                            autoplay
                            style={{ height: 200, width: 200 }}
                        />}
                        title="Twoje konto oczekuje na zatwierdzenie"
                        subTitle="Administrator sprawdza Twoje dokumenty. Proszę czekać."
                        extra={[
                            <Space direction="vertical" key="extra" style={{ width: '100%' }}>
                                <Badge
                                    count={countdown}
                                    style={{ backgroundColor: '#52c41a' }}
                                    showZero
                                >
                                    <Text>Automatyczne wylogowanie za</Text>
                                </Badge>
                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={handleLogout}
                                    icon={<ClockCircleOutlined />}
                                    block={isMobile}
                                    style={isMobile ? mobileStyles.button : {}}
                                >
                                    Wyloguj się teraz
                                </Button>
                            </Space>
                        ]}
                    />
                </Modal>
            )}

            {userStatus === 'Oczekuje na podpis dokumentów' && isModalVisible && (
                <DocumentSigning
                    visible={isModalVisible}
                    onClose={() => setIsModalVisible(false)}
                />
            )}

            {/* ZMODYFIKOWANY MODAL - Wymagana weryfikacja */}
            {userStatus === 'Wymagana weryfikacja' && isModalVisible && (
                <Modal
                    open={true}
                    closable={false}
                    footer={null}
                    maskClosable={false}
                    keyboard={false}
                    width={isMobile ? '100%' : 600}
                >
                    <Result
                        icon={<Lottie
                            animationData={awaitingApprovalAnimation}
                            loop={true}
                            autoplay
                            style={{ height: 200, width: 200 }}
                        />}
                        title="Trwa generowanie dokumentów"
                        subTitle={isGeneratingDocuments ? "Proszę czekać, dokumenty są generowane..." : "Przygotowywanie dokumentów do podpisu..."}
                        extra={isGeneratingDocuments && (
                            <Progress
                                percent={undefined}
                                status="active"
                                strokeColor="#1890ff"
                                style={{ maxWidth: '300px', margin: '0 auto' }}
                            />
                        )}
                    />
                </Modal>
            )}

            {/* Sumsub Modal */}
            <Modal
                open={isSumsubModalVisible}
                onCancel={() => {
                    setIsSumsubModalVisible(false);
                    setIsModalVisible(true);
                }}
                footer={null}
                maskClosable={false}
                keyboard={false}
                width={isMobile ? '100%' : 900}
                styles={{
                    body: { minHeight: '600px', padding: 0 },  // ✅ NOWE
                    mask: { zIndex: 1001 }  // ✅ NOWE
                }}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100%' } : {}}
                zIndex={1002}
            >
                <Spin spinning={isLoadingSumsub} tip="Ładowanie weryfikacji..." size="large">
                    <div id="sumsub-container" style={{ width: '100%', minHeight: '600px' }}></div>
                </Spin>
            </Modal>
        </UserStatusContext.Provider>
    );
};

export default UserStatusProvider;